import { z } from 'zod';
/**
 * Site content blocks.
 *
 * Every piece of copy on the marketing site — and the marketing rail on the
 * admin sign-in screen — is one of these blocks: a keyed JSON document in
 * `LandingContent`, validated by the schema below, edited in the Super Admin
 * console and read by the Next.js app on a one-minute revalidation window.
 *
 * The schemas carry **no content defaults**. A block that is not published
 * simply does not render; the words live in the database (the seed writes the
 * first version) and nowhere in a source file. That is the whole point: a
 * headline is changed by a marketing person in a form, not by a deploy.
 *
 * What a schema does own is *shape* — how many items a grid holds, that a
 * link has a label and a destination, that a module card picks its icon from
 * the set the site can draw. Those are rendering constraints, and they belong
 * beside the code that renders.
 */
const text = (max) => z.string().max(max);
const LinkSchema = z.object({
    label: text(80),
    href: text(300),
});
/**
 * Icons a block may ask for, by name.
 *
 * The site maps each to a `lucide-react` component. A closed list rather than
 * a free string, so a typo in the console renders a visible validation error
 * rather than a silent blank square.
 */
export const CONTENT_ICONS = [
    'IndianRupee',
    'Wrench',
    'ShieldCheck',
    'BarChart3',
    'CalendarDays',
    'Users',
    'FileText',
    'Siren',
    'Building2',
    'LayoutGrid',
    'Palette',
    'TrendingUp',
    'MessageSquare',
    'Phone',
    'Mail',
    'MapPin',
    'Clock',
    'Shield',
    'Sparkles',
    'CheckCircle2',
];
const IconSchema = z.enum(CONTENT_ICONS);
/** The three accent tones the design system offers a card. */
export const CONTENT_TONES = ['indigo', 'teal', 'marigold'];
const ToneSchema = z.enum(CONTENT_TONES);
// ── Home ────────────────────────────────────────────────────────────────────
export const HomeHeroSchema = z.object({
    badge: text(80),
    title: text(120),
    subtitle: text(400),
    primaryCta: LinkSchema,
    secondaryCta: LinkSchema,
    /** The short reassurances under the buttons — "No card needed". */
    assurances: z.array(text(60)).max(5),
    /** The lines that promise a trial, as they read when the platform offers none. */
    paid: z
        .object({ primaryCtaLabel: text(80), assurances: z.array(text(60)).max(5) })
        .default({ primaryCtaLabel: 'Get started', assurances: ['Pay as you go, monthly or yearly', 'Set up in an afternoon', 'Data stays in India'] }),
});
/** The sample screen inside the hero's phone illustration. */
export const HomePhoneSchema = z.object({
    societyLine: text(60),
    greeting: text(40),
    initials: text(3),
    liveLabel: text(30),
    liveTime: text(20),
    visitorName: text(40),
    visitorNote: text(60),
    approveLabel: text(20),
    denyLabel: text(20),
    /** Four quick-action labels; the icons are fixed by position. */
    actions: z.array(text(20)).length(4),
    dueLabel: text(40),
    dueAmount: text(20),
    dueBadge: text(30),
    dueBreakdown: text(80),
    dueLate: text(30),
    payLabel: text(20),
    pulse: z.array(z.object({ value: text(8), label: text(30) })).length(3),
});
export const HomeStatsSchema = z.object({
    items: z.array(z.object({ value: text(24), label: text(120) })).max(8),
});
export const HomeProblemsSchema = z.object({
    eyebrow: text(60),
    title: text(120),
    description: text(300),
    items: z.array(z.object({ title: text(100), body: text(400) })).max(8),
});
export const HomeModulesSchema = z.object({
    eyebrow: text(60),
    title: text(120),
    description: text(300),
    items: z
        .array(z.object({ icon: IconSchema, title: text(100), body: text(400), tone: ToneSchema }))
        .max(12),
});
export const HomeWhySchema = z.object({
    eyebrow: text(60),
    title: text(120),
    items: z.array(z.object({ icon: IconSchema, title: text(120), body: text(400) })).max(6),
});
export const HomeTestimonialsSchema = z.object({
    eyebrow: text(60),
    title: text(120),
});
export const HomeCtaSchema = z.object({
    title: text(120),
    body: text(400),
    primaryCta: LinkSchema,
    secondaryCta: LinkSchema,
    note: text(160),
    /** The same section when the platform offers no free trial. */
    paid: z
        .object({ title: text(120), body: text(400), primaryCtaLabel: text(80), note: text(160) })
        .default({
        title: 'Put your society on KinshipOS this month',
        body: "Add your blocks and flats, invite a handful of residents, and raise one month's bills. Priced per flat, monthly or yearly, and you can cancel from the panel whenever you like.",
        primaryCtaLabel: 'Get started',
        note: 'No setup fee. Cancel whenever you like.',
    }),
});
// ── Pricing ─────────────────────────────────────────────────────────────────
/** The `<title>` and description a page is shared and indexed with. */
const PageMetaSchema = z.object({
    metaTitle: text(70),
    metaDescription: text(160),
});
export const PricingPageSchema = PageMetaSchema.extend({
    eyebrow: text(60),
    title: text(120),
    subtitle: text(300),
    comparisonTitle: text(120),
    comparisonDescription: text(300),
    faqTitle: text(120),
    closingTitle: text(120),
    closingBody: text(300),
    closingCta: LinkSchema,
    /** The lines that mention the trial, as they read when the platform offers none. */
    paid: z
        .object({ metaDescription: text(300), subtitle: text(300), closingTitle: text(120), closingBody: text(300), closingCtaLabel: text(80) })
        .default({
        metaDescription: 'Priced per flat, monthly or yearly, no setup fee. See exactly what your society would pay.',
        subtitle: "Move the slider to your society's size and see exactly what you would pay. No setup fee; cancel whenever you like.",
        closingTitle: 'Still not sure which plan?',
        closingBody: 'Start on Growth — it fits most societies — and change plans from the panel at any time.',
        closingCtaLabel: 'Get started',
    }),
    /** Under the plan cards — the GST and add-on note. */
    disclaimer: text(300),
    /** The quick-pick society sizes on the calculator. */
    presets: z.array(z.object({ flats: z.number().int().min(1).max(50_000), label: text(40) })).max(6),
    /** featureKey -> the short label shown on a plan card. */
    featureLabels: z.record(text(60), text(60)),
    /** The calculator's own words. `{flats}`, `{amount}` and `{max}` are filled in. */
    calculator: z.object({
        question: text(120),
        unit: text(30),
        monthly: text(30),
        yearly: text(30),
        yearlyBadge: text(30),
        bestFit: text(80),
        perMonth: text(40),
        perYear: text(40),
        saves: text(80),
        perFlat: text(60),
        tooSmall: text(120),
        /** The card button while the platform offers a free trial. */
        cta: text(40),
        /** The card button when it does not — the society pays after signing up. */
        ctaBuy: text(40).default('Get started'),
        /** Under a card's highlights when the plan has more; `{count}` is filled in. */
        moreFeatures: text(60),
    }),
});
export const PricingFaqSchema = z.object({
    items: z.array(z.object({ question: text(160), answer: text(800) })).max(20),
});
export const PricingFeaturesSchema = z.object({
    groups: z
        .array(z.object({
        group: text(60),
        rows: z.array(z.object({ key: text(60), label: text(120) })).max(30),
    }))
        .max(8),
});
// ── Navigation ──────────────────────────────────────────────────────────────
export const NavHeaderSchema = z.object({
    links: z.array(LinkSchema).max(8),
    signInLabel: text(40),
    /** The header button while the platform offers a free trial. */
    ctaLabel: text(40),
    /** The header button when it does not. */
    ctaLabelPaid: text(40).default('Get started'),
});
export const NavFooterSchema = z.object({
    columns: z.array(z.object({ heading: text(40), links: z.array(LinkSchema).max(10) })).max(4),
    /** The lines under the tagline — where the data lives, where the company is. */
    notes: z.array(z.object({ icon: IconSchema, text: text(160) })).max(4),
    legalHeading: text(40),
    /** Above the app-store buttons, when any store link is set. */
    appsHeading: text(40),
    /** "Grievance officer:" — precedes the support address in the bottom bar. */
    grievanceLabel: text(40),
    /** Follows the grievance-officer address in the bottom bar. */
    grievanceNote: text(200),
});
// ── Standalone pages ────────────────────────────────────────────────────────
export const CONTACT_CHANNELS = ['whatsapp', 'phone', 'email'];
export const ContactPageSchema = PageMetaSchema.extend({
    title: text(120),
    intro: text(300),
    channels: z
        .array(z.object({
        kind: z.enum(CONTACT_CHANNELS),
        title: text(60),
        body: text(200),
        /** What is printed — "+91 98290 00000", "hello@kinshipos.com". */
        value: text(120),
        /** What is linked — a wa.me URL, tel:, mailto:. */
        href: text(300),
    }))
        .max(4),
    demoTitle: text(80),
    demoBody: text(400),
    demoCta: LinkSchema,
    officeTitle: text(80),
    addressLines: z.array(text(120)).max(6),
    /** "Existing customer with a problem?" */
    supportNote: text(400),
});
export const BuildersPageSchema = PageMetaSchema.extend({
    hero: z.object({
        eyebrow: text(60),
        title: text(120),
        body: text(500),
        primaryCta: LinkSchema,
        secondaryCta: LinkSchema,
    }),
    benefits: z.object({
        eyebrow: text(60),
        title: text(120),
        items: z.array(z.object({ icon: IconSchema, title: text(100), body: text(400) })).max(8),
    }),
    commercials: z.object({
        eyebrow: text(60),
        title: text(120),
        items: z.array(z.object({ title: text(100), body: text(400) })).max(6),
        /** "Enterprise pricing is quoted per portfolio." — then the link, then the suffix. */
        footnote: text(300),
        footnoteCta: LinkSchema,
        footnoteSuffix: text(200),
    }),
});
export const SignupPageSchema = PageMetaSchema.extend({
    title: text(120),
    subtitle: text(300),
    /** The same three lines when the platform offers no free trial. */
    paid: z
        .object({ metaTitle: text(120), title: text(120), subtitle: text(300) })
        .default({
        metaTitle: 'Create your society',
        title: 'Create your society',
        subtitle: 'Sign up, pick a plan and pay — your society is live the moment the payment lands.',
    }),
    /** Precedes the terms and privacy links under the form. */
    consentNote: text(200),
    /** Follows them — "We will send one SMS to verify your number." */
    smsNote: text(200),
    railTitle: text(80),
    steps: z.array(z.object({ title: text(80), body: text(300) })).max(6),
    assurances: z.array(z.object({ icon: IconSchema, text: text(120) })).max(4),
});
export const BlogPageSchema = PageMetaSchema.extend({
    title: text(80),
    description: text(300),
    allLabel: text(40),
    emptyText: text(200),
    emptyCategoryText: text(200),
    featuredLabel: text(40),
    /** On a post: the way back, the reading time (`{minutes}` filled in), the share row. */
    backLabel: text(40),
    readTime: text(40),
    shareLabel: text(40),
    shareOn: text(60),
    copyLink: text(40),
    linkCopied: text(40),
});
/** A form field's words. */
const FieldCopySchema = z.object({ label: text(60), placeholder: text(80), hint: text(160) });
export const SOCIETY_TYPE_VALUES = ['APARTMENT', 'VILLA', 'GATED_TOWNSHIP', 'ROW_HOUSE', 'MIXED'];
/** The sign-up form: every label, placeholder, hint, option and message. */
export const SignupFormSchema = z.object({
    societyName: FieldCopySchema,
    adminName: FieldCopySchema,
    phone: FieldCopySchema,
    email: FieldCopySchema,
    address: FieldCopySchema,
    city: FieldCopySchema,
    state: FieldCopySchema,
    pincode: FieldCopySchema,
    flats: FieldCopySchema,
    societyType: FieldCopySchema,
    country: FieldCopySchema,
    /** The pickers' "nothing found" line (country, state, city). */
    searchEmptyText: text(80).default('Nothing matches.'),
    /** Under the button when the platform offers no free trial. */
    paidNote: text(200).default('After verifying your number you pick a plan and pay; your society is live as soon as the payment lands.'),
    /** Shown in the city list for a town the dataset does not know. */
    cityOther: text(60),
    societyTypes: z.array(z.object({ value: z.enum(SOCIETY_TYPE_VALUES), label: text(60) })).min(1).max(5),
    sendCode: text(60),
    /** `{phone}` is the masked number. */
    codeSent: text(120),
    codeLabel: text(60),
    changeDetails: text(40),
    createSociety: text(60),
    /** Only in development; `{code}` is the OTP. */
    devCode: text(120),
    networkError: text(200),
    genericError: text(200),
});
const ConsentCategoryCopySchema = z.object({
    label: text(60),
    description: text(400).min(20),
    /** Art. 13(1)(e) — who actually receives the data. "Third parties" is not a recipient. */
    recipients: z.array(text(80)).min(1).max(10),
});
/** The cookie banner and its choices dialog. The layout is fixed by law; the words are yours. */
export const SiteConsentSchema = z.object({
    banner: text(400),
    privacyLink: text(60),
    rejectAll: text(40),
    acceptAll: text(40),
    choose: text(40),
    dialogTitle: text(80),
    dialogNote: text(200),
    /** `{recipients}` is the comma-separated list. */
    sharedWith: text(60),
    saveChoices: text(40),
    closeLabel: text(60),
    footerLink: text(40),
    categories: z.object({
        necessary: ConsentCategoryCopySchema,
        analytics: ConsentCategoryCopySchema,
        marketing: ConsentCategoryCopySchema,
    }),
});
/**
 * The site's small words: screen-reader labels, table headings, the theme
 * toggle, the metadata title of a missing page. `{site}`, `{network}` and
 * `{theme}` are filled in.
 */
export const SiteChromeSchema = z.object({
    skipToContent: text(40),
    mainNav: text(40),
    mobileNav: text(40),
    homeLink: text(60),
    socialLink: text(60),
    phoneMockup: text(120),
    statsHeading: text(60),
    blogCategories: text(40),
    blogTags: text(40),
    pricingFeatureHeader: text(40),
    included: text(40),
    notIncluded: text(40),
    notFoundTitle: text(60),
    themeLight: text(40),
    themeDark: text(40),
    themeSystem: text(40),
    themeSwitch: text(60),
});
export const SiteNotFoundSchema = z.object({
    title: text(80),
    body: text(300),
    primary: LinkSchema,
    secondary: LinkSchema,
});
// ── Admin panel ─────────────────────────────────────────────────────────────
/** The marketing rail beside the society sign-in form. */
export const AdminLoginSchema = z.object({
    title: text(120),
    subtitle: text(300),
    bullets: z.array(z.object({ icon: IconSchema, text: text(120) })).max(4),
    quote: text(300),
    quoteAuthor: text(120),
});
// ── Resident app ────────────────────────────────────────────────────────────
//
// The resident app carries no copy of its own for anything a society or the
// platform might want to change without an app-store release: the home
// screen's shortcuts, the welcome tour, the languages on offer, the UPI apps
// on the pay sheet, the bazaar categories, the join-request relationships and
// the support addresses. The app reads these from `/public/blocks` before
// sign-in and keeps the last copy for an offline launch.
/** The shortcuts the app can draw on the home screen, by name. */
export const RESIDENT_ACTIONS = [
    'preApprove',
    'payDues',
    'complaint',
    'book',
    'helpers',
    'notices',
    'documents',
    'bazaar',
    'sos',
    'notifications',
];
/** The locales the app ships translations for. A language not in this list has no strings. */
export const RESIDENT_LOCALES = ['en', 'hi', 'mr', 'gu', 'ta', 'te', 'kn'];
/** How a resident may be related to a flat when asking to join. Mirrors the API's join enum. */
export const RESIDENT_RELATIONSHIPS = ['OWNER', 'TENANT', 'FAMILY_MEMBER', 'OWNER_NOT_RESIDING'];
const HexColour = z.string().regex(/^#[0-9a-fA-F]{6}$/, 'A hex colour like #5F259F');
export const ResidentHomeSchema = z.object({
    quickActionsTitle: text(40),
    quickActions: z.array(z.object({ action: z.enum(RESIDENT_ACTIONS), label: text(20) })).max(8),
    pulseTitle: text(40),
    /** The "+" button's sheet: a title and up to four shortcuts with a line of help each. */
    startTitle: text(40),
    startActions: z
        .array(z.object({ action: z.enum(RESIDENT_ACTIONS), title: text(40), subtitle: text(80) }))
        .max(4),
    /** The "Powered by" line under the home screen. */
    poweredByLabel: text(40),
    poweredByName: text(60),
    poweredByUrl: text(300),
});
export const ResidentWelcomeSchema = z.object({
    /** May use `{name}` and `{society}`; the society name is highlighted. */
    title: text(120),
    subtitle: text(300),
    bullets: z
        .array(z.object({ action: z.enum(RESIDENT_ACTIONS), title: text(80), subtitle: text(120) }))
        .max(4),
    ctaLabel: text(40),
});
export const ResidentLanguagesSchema = z.object({
    title: text(80),
    subtitle: text(200),
    /** Suffix on a language that has no strings yet — "soon". */
    comingSoonLabel: text(30),
    /** Shown in this order; `supported` false renders as "coming soon" and cannot be picked. */
    languages: z
        .array(z.object({
        code: text(8),
        native: text(40),
        english: text(40),
        supported: z.boolean(),
    }))
        .max(12),
});
export const ResidentJoinSchema = z.object({
    relationships: z.array(z.object({ value: z.enum(RESIDENT_RELATIONSHIPS), label: text(40) })).max(4),
});
export const ResidentBazaarSchema = z.object({
    /** The filter that shows every listing. */
    allLabel: text(30),
    /** The filter that shows only give-aways. */
    freeLabel: text(30),
    /** What a listing can be filed under — also the choices when posting one. */
    categories: z.array(text(30)).min(1).max(12),
});
export const ResidentPaymentsSchema = z.object({
    /** Under the pay button — "Secured by Razorpay · 0% convenience fee". */
    securedLine: text(120),
    /** In the payment sheet — the longer reassurance. */
    securedNote: text(200),
    /** UPI apps offered on the pay sheet — the Android package the intent targets. */
    upiApps: z
        .array(z.object({
        packageName: text(120),
        name: text(30),
        /** One to three letters drawn as the app's mark — "G", "Pe". */
        mark: text(3),
        background: HexColour,
        foreground: HexColour,
        markBackground: HexColour,
    }))
        .max(8),
});
/** The kinds of daily helper a resident can register. Mirrors the API's HelperType. */
export const RESIDENT_HELPER_TYPES = [
    'MAID',
    'COOK',
    'DRIVER',
    'NANNY',
    'GARDENER',
    'NEWSPAPER',
    'MILKMAN',
    'LAUNDRY',
    'PLUMBER',
    'ELECTRICIAN',
    'CARPENTER',
    'OTHER',
];
export const ResidentHelpersSchema = z.object({
    /** Shown in this order on the add-helper form; at most eight fit the grid. */
    roles: z.array(z.object({ type: z.enum(RESIDENT_HELPER_TYPES), label: text(20) })).min(1).max(8),
});
/**
 * Every sentence the resident app shows, keyed the way the app's ARB files
 * key them. A key left out falls back to the copy compiled into the app, so
 * a society only publishes what it wants to change. One row per locale.
 */
export const ResidentStringsSchema = z.record(z.string().regex(/^[a-z][A-Za-z0-9]*$/), text(600));
export const ResidentSupportSchema = z.object({
    /** ISO 3166-1 alpha-2 — the country the phone field starts on before sign-in. */
    defaultCountry: z.string().regex(/^[A-Z]{2}$/, 'Two-letter country code'),
    /** Where a resident writes when the society has no grievance officer of its own. */
    privacyEmail: text(120),
    privacyPolicyUrl: text(300),
    privacyLinkLabel: text(40),
    termsUrl: text(300),
    termsLinkLabel: text(40),
    helpUrl: text(300),
    /** Under the sign-in buttons; `{terms}` and `{privacy}` become the links. */
    legalLine: text(200),
    /** At the foot of the privacy centre — who processes, who is the fiduciary. */
    processorNote: text(400),
});
// ── Registry ────────────────────────────────────────────────────────────────
export const CONTENT_BLOCK_SCHEMAS = {
    'home.hero': HomeHeroSchema,
    'home.phone': HomePhoneSchema,
    'home.stats': HomeStatsSchema,
    'home.problems': HomeProblemsSchema,
    'home.modules': HomeModulesSchema,
    'home.why': HomeWhySchema,
    'home.testimonials': HomeTestimonialsSchema,
    'home.cta': HomeCtaSchema,
    'pricing.page': PricingPageSchema,
    'pricing.faq': PricingFaqSchema,
    'pricing.features': PricingFeaturesSchema,
    'nav.header': NavHeaderSchema,
    'nav.footer': NavFooterSchema,
    'contact.page': ContactPageSchema,
    'builders.page': BuildersPageSchema,
    'signup.page': SignupPageSchema,
    'blog.page': BlogPageSchema,
    'signup.form': SignupFormSchema,
    'site.consent': SiteConsentSchema,
    'site.notFound': SiteNotFoundSchema,
    'site.chrome': SiteChromeSchema,
    'admin.login': AdminLoginSchema,
    'resident.home': ResidentHomeSchema,
    'resident.welcome': ResidentWelcomeSchema,
    'resident.languages': ResidentLanguagesSchema,
    'resident.join': ResidentJoinSchema,
    'resident.bazaar': ResidentBazaarSchema,
    'resident.payments': ResidentPaymentsSchema,
    'resident.support': ResidentSupportSchema,
    'resident.helpers': ResidentHelpersSchema,
    'resident.strings': ResidentStringsSchema,
};
export const CONTENT_BLOCK_KEYS = Object.keys(CONTENT_BLOCK_SCHEMAS);
export function isContentBlockKey(value) {
    return Object.prototype.hasOwnProperty.call(CONTENT_BLOCK_SCHEMAS, value);
}
//# sourceMappingURL=content-blocks.js.map